import { ILeadStageOption } from '@app/features/crm/models/lead.model'
import { ELeadStageTitle, IValidationRule } from '../enum/client.enum'
import { EValidationType } from '../enum/client.enum'
import { EDocumentIcon } from '../enum/document.enum'

export const ROUTE_PREFIXES = {
    SCHOOL_SITE: '/school-site',
} as const

export const AppConstants = {
    LAKH_CONVERSION_CONSTANT: 100000,
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    NEW_SCHOOL: 'New School',
    SUB_APP_ID: '6',
    HEARTBEAT_INTERVAL: 30000,
    AUTO_CLOSE_DIALOG_TIMEOUT_MS: 3000,
    CHECK: 'check',
    EDIT: 'edit',
    IN: 'in',
    OUT: 'out',
    // Communication filter constants
    COMMUNICATION_FILTER_ALL: 'All',
    COMMUNICATION_FILTER_CALLS: 'Calls',
    COMMUNICATION_FILTER_EMAILS: 'Emails',
    COMMUNICATION_FILTER_LINKEDIN: 'LinkedIn',
    COMMUNICATION_FILTER_IN_PERSON: 'In Person',
    // Leaderboard labels
    LEADERBOARD_TITLE: 'Monthly Leaderboard',
    LEADERBOARD_OPTION_DEMOS: 'Most Demos',
    LEADERBOARD_OPTION_VISITS: 'Most Visits',
    LEADERBOARD_OPTION_PROPOSALS: 'Most Proposals',
    LEADERBOARD_OPTION_CONVERTED: 'Most Converted',
    LEADERBOARD_ERROR_MESSAGE: 'Failed to load monthly leaderboard',
    SALES_CYCLE_LABEL: 'Sales Cycle',
    SHIKSHA_SCOPE_LABEL: 'View Scope:',
    REFRESH_LABEL_UPDATE_INTERVAL_MS: 60_000,
    SHIKSHA_REFRESH_LABEL_MAX_RELATIVE_MINUTES: 60,
    // Chart widget titles
    LEAD_SOURCES_TITLE: 'Lead Sources',
    NEW_LEADS_CREATED_TITLE: 'New Leads Created',
    // Chart data paths
    LEAD_SOURCES_DATA_PATH: 'assets/json/lead-sources-data.json',
    NEW_LEADS_CREATED_DATA_PATH: 'assets/json/new-leads-created-data.json',
    // Chart filter labels
    SALES_REP_LABEL: 'Sales Rep',
    CITY_LABEL: 'City',
    PRODUCT_LABEL: 'Product',
    ALL_REPS: 'All Sales Representatives',
    ALL_CITIES: 'All Cities',
    ALL_PRODUCTS: 'All Products',
    // Client type descriptions for tooltips
    NEW_CLIENTS_DESCRIPTION: 'Completely new customers who have never used Ei products.',
    EXISTING_CLIENTS_DESCRIPTION: 'Customers who are currently using one or more Ei products.',
    PAST_CLIENTS_DESCRIPTION:
        "Customers who have used an Ei product before but aren't using any currently.",
    /**
     * Fixed app header height (px). Must stay aligned with `$header-height` in
     * `src/styles/_variables.scss`.
     */
    FIXED_HEADER_HEIGHT_PX: 64,
    /**
     * Viewport inset (px) for CDK-connected dropdown/select overlays on left, right, and bottom.
     */
    OVERLAY_VIEWPORT_EDGE_INSET_PX: 8,
}

/** @see AppConstants.FIXED_HEADER_HEIGHT_PX */
export const APP_FIXED_HEADER_HEIGHT_PX = AppConstants.FIXED_HEADER_HEIGHT_PX

/**
 * CDK `FlexibleConnectedPositionStrategy.withViewportMargin`: reserve the fixed app header at the
 * top without over-shrinking horizontal usable area.
 */
export const APP_DROPDOWN_OVERLAY_VIEWPORT_MARGIN = {
    top: AppConstants.FIXED_HEADER_HEIGHT_PX,
    bottom: AppConstants.OVERLAY_VIEWPORT_EDGE_INSET_PX,
    left: AppConstants.OVERLAY_VIEWPORT_EDGE_INSET_PX,
    right: AppConstants.OVERLAY_VIEWPORT_EDGE_INSET_PX,
} as const

export const MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
] as const

/** Angular `DatePipe` / `formatDate` pattern for navigator dates (e.g. 13-May-2026). */
export const NAVIGATOR_DATE_DISPLAY_FORMAT = 'dd-MMM-yyyy' as const

/** Same token layout as `NAVIGATOR_DATE_DISPLAY_FORMAT`; use with date-fns `format`. */
export const NAVIGATOR_DATE_FNS_FORMAT = NAVIGATOR_DATE_DISPLAY_FORMAT

/** Datetime variants for pipes, date-fns, and document metadata. */
export const NAVIGATOR_DATETIME_DISPLAY_12H = 'dd-MMM-yyyy, hh:mm a' as const
export const NAVIGATOR_DATETIME_DISPLAY_24H = 'dd-MMM-yyyy, HH:mm' as const

export const DATE_INPUT_FORMAT_PLACEHOLDER = 'DD-MMM-YYYY' as const

// SVG Icon Paths
export const ICON_PATHS = {
    RIGHT_ARROW: 'assets/images/icons/right-arrow.svg',
    ALERT_CIRCLE: 'assets/images/icons/alert-circle-icon.svg',
    CHECK_CIRCLE: 'assets/images/icons/check-circle-icon.svg',
    CLOSE: 'assets/icons/close-icon.svg',
    TRENDING_UP: 'assets/images/icons/trending-up.svg',
} as const

// Lead Dashboard query param keys
export const LEAD_DASHBOARD_QUERY_PARAMS = {
    USER_ID: 'user_id',
    LEAD_IDS: 'lead_ids',
    SOURCE: 'source',
    VIEW: 'view',
    TEAM_PIPELINE: 'team_pipeline',
    TEAM_MEMBER_ID: 'team_member_id',
} as const

// CRM team role labels
export const TEAM_ROLE_LABELS = {
    REGIONAL_MANAGER: 'Regional Manager',
    ZONAL_MANAGER: 'Zonal Manager',
    ACADEMIC_CONSULTANT: 'Academic Consultant',
} as const

// Client Dashboard query param keys (funnel-filtered view)
export const CLIENT_DASHBOARD_QUERY_PARAMS = {
    CLIENT_IDS: 'client_ids',
    DRILLDOWN_KEY: 'drilldown_key',
    LOAD_ALL: 'load_all',
    SOURCE: 'source',
    VIEW: 'view',
} as const

export const TEAM_ASSIGNED_SCHOOLS_DRILLDOWN_STORAGE_PREFIX = 'team_s0_assigned_schools'

// Home dashboard query param keys
export const HOME_DASHBOARD_QUERY_PARAMS = {
    TAB: 'home_tab',
} as const

// Funnel source value for back navigation
export const FUNNEL_SOURCE_HOME = 'funnel' as const

/**
 * HTML Sanitization Configuration Constants
 */
export const HTML_SANITIZER_CONFIG = {
    /**
     * Default allowed HTML tags for general content
     */
    DEFAULT_ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'blockquote',
        'code',
        'pre',
        'span',
        'div',
        'table',
        'thead',
        'tbody',
        'tr',
        'td',
        'th',
        'a',
        'img',
    ],

    /**
     * Default allowed HTML attributes
     */
    DEFAULT_ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'class',
        'id',
        'style',
        'target',
        'rel',
        'width',
        'height',
    ],

    /**
     * Rich text editor allowed HTML tags (more permissive)
     */
    RICH_TEXT_ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'b',
        'i',
        's',
        'del',
        'ins',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'h7',
        'ul',
        'ol',
        'li',
        'dl',
        'dt',
        'dd',
        'blockquote',
        'code',
        'pre',
        'kbd',
        'samp',
        'var',
        'span',
        'div',
        'section',
        'article',
        'header',
        'footer',
        'table',
        'thead',
        'tbody',
        'tfoot',
        'tr',
        'td',
        'th',
        'caption',
        'a',
        'img',
        'figure',
        'figcaption',
        'mark',
        'small',
        'sub',
        'sup',
        'time',
        'abbr',
        'acronym',
        'cite',
        'q',
        'dfn',
        'address',
    ],

    /**
     * Rich text editor allowed HTML attributes
     */
    RICH_TEXT_ALLOWED_ATTR: [
        'href',
        'src',
        'alt',
        'title',
        'class',
        'id',
        'style',
        'target',
        'rel',
        'width',
        'height',
        'data-*',
        'lang',
        'dir',
        'cite',
        'datetime',
        'type',
        'value',
        'start',
        'reversed',
        'role',
        'aria-*',
    ],

    /**
     * Email allowed HTML tags (strict)
     */
    EMAIL_ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'em',
        'u',
        'b',
        'i',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'blockquote',
        'span',
        'div',
        'table',
        'thead',
        'tbody',
        'tr',
        'td',
        'th',
        'a',
    ],

    /**
     * Email allowed HTML attributes
     */
    EMAIL_ALLOWED_ATTR: ['href', 'class', 'style', 'target', 'rel'],

    /**
     * Forbidden HTML tags for email sanitization
     */
    EMAIL_FORBIDDEN_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],

    /**
     * Forbidden HTML attributes for email sanitization
     */
    EMAIL_FORBIDDEN_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
} as const

export const MAXLENGTH = 200
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/**
 * Maps file types to their corresponding Material Design icons
 */
export const FILE_ICON_MAP: Record<string, string> = {
    pdf: EDocumentIcon.PDF,
    ppt: EDocumentIcon.SLIDESHOW,
    pptx: EDocumentIcon.SLIDESHOW,
    doc: EDocumentIcon.DESCRIPTION,
    docx: EDocumentIcon.DESCRIPTION,
    xls: EDocumentIcon.TABLE_CHART,
    xlsx: EDocumentIcon.TABLE_CHART,
    jpg: EDocumentIcon.IMAGE,
    jpeg: EDocumentIcon.IMAGE,
    png: EDocumentIcon.IMAGE,
}
export const FILE_SIZE_UNITS = ['Bytes', 'KB', 'MB', 'GB'] as const
// Custom date format for DD/MM/YYYY
export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
}

// Validation Constants
export const VALIDATION_RULES: Record<string, IValidationRule[]> = {
    'Client Name': [
        { type: EValidationType.REQUIRED, message: 'Customer Name is required' },
        { type: EValidationType.STRING, message: 'Customer Name must be text' },
        {
            type: EValidationType.MIN_LENGTH,
            value: 3,
            message: 'Customer Name must be at least 3 characters',
        },
        {
            type: EValidationType.PATTERN,
            value: /^[a-zA-Z0-9\s.&-]+$/,
            message:
                'Customer Name can only contain letters, numbers, spaces, dots, hyphens, and ampersands',
        },
    ],
    "Student's Name": [
        { type: EValidationType.REQUIRED, message: "Student's Name is required" },
        { type: EValidationType.STRING, message: "Student's Name must be text" },
        {
            type: EValidationType.MIN_LENGTH,
            value: 3,
            message: "Student's Name must be at least 3 characters",
        },
        {
            type: EValidationType.PATTERN,
            value: /^[a-zA-Z0-9\s.-]+$/,
            message: "Student's Name can only contain letters, numbers, spaces, dots, and hyphens",
        },
    ],
    Curriculum: [
        { type: EValidationType.REQUIRED, message: 'Curriculum is required' },
        { type: EValidationType.STRING, message: 'Curriculum must be text' },
    ],
    'Interested Products': [
        { type: EValidationType.STRING, message: 'Interested Products must be text' },
        {
            type: EValidationType.PATTERN,
            value: /^\{\s*"edtech"\s*:\s*(true|false)\s*,\s*"library"\s*:\s*(true|false)\s*,\s*"smart_class"\s*:\s*(true|false)\s*\}$/i,
            message:
                'Interested Products must be JSON: {"edtech": true/false, "library": true/false, "smart_class": true/false}',
        },
    ],
    'School Name': [
        { type: EValidationType.REQUIRED, message: 'School Name is required' },
        { type: EValidationType.STRING, message: 'School Name must be text' },
    ],
    Class: [
        { type: EValidationType.REQUIRED, message: 'Class is required' },
        { type: EValidationType.STRING, message: 'Class must be text' },
    ],
    Website: [
        {
            type: EValidationType.URL,
            message: 'Website must be a valid URL (e.g., https://example.com)',
        },
    ],
    'Email ID': [{ type: EValidationType.EMAIL, message: 'Email ID must be in valid format' }],
    'Contact Name': [{ type: EValidationType.STRING, message: 'Contact Name must be text' }],
    'Role/Designation': [
        { type: EValidationType.STRING, message: 'Role/Designation must be text' },
    ],

    'Billing Pincode': [
        { type: EValidationType.PINCODE, message: 'Billing Pincode must be a 6-digit number' },
    ],
    'Student Strength': [
        { type: EValidationType.NUMERIC, message: 'Student Strength must be a number' },
        {
            type: EValidationType.MIN_VALUE,
            value: 1,
            message: 'Student Strength must be at least 1',
        },
    ],
    '10th Board Result (%)': [
        { type: EValidationType.NUMERIC, message: '10th Board Result (%) must be a number' },
        {
            type: EValidationType.MIN_VALUE,
            value: 0,
            message: '10th Board Result (%) must be at least 0',
        },
        {
            type: EValidationType.MAX_VALUE,
            value: 100,
            message: '10th Board Result (%) must be at most 100',
        },
        { type: EValidationType.DECIMAL, message: '10th Board Result (%) can be a decimal number' },
    ],
    'Annual Fee (₹)': [
        { type: EValidationType.NUMERIC, message: 'Annual Fee (₹) must be a number' },
        { type: EValidationType.MIN_VALUE, value: 0, message: 'Annual Fee (₹) must be at least 0' },
    ],
    'Tuition Fee (₹)': [
        { type: EValidationType.NUMERIC, message: 'Tuition Fee (₹) must be a number' },
        {
            type: EValidationType.MIN_VALUE,
            value: 0,
            message: 'Tuition Fee (₹) must be at least 0',
        },
    ],
    'Tech Fee (₹)': [
        { type: EValidationType.NUMERIC, message: 'Tech Fee (₹) must be a number' },
        { type: EValidationType.MIN_VALUE, value: 0, message: 'Tech Fee (₹) must be at least 0' },
    ],
    'Smart Class Fee (₹)': [
        { type: EValidationType.NUMERIC, message: 'Smart Class Fee (₹) must be a number' },
        {
            type: EValidationType.MIN_VALUE,
            value: 0,
            message: 'Smart Class Fee (₹) must be at least 0',
        },
    ],
    'Development Fee (₹)': [
        { type: EValidationType.NUMERIC, message: 'Development Fee (₹) must be a number' },
        {
            type: EValidationType.MIN_VALUE,
            value: 0,
            message: 'Development Fee (₹) must be at least 0',
        },
    ],
    'Other Fee (₹)': [
        { type: EValidationType.NUMERIC, message: 'Other Fee (₹) must be a number' },
        { type: EValidationType.MIN_VALUE, value: 0, message: 'Other Fee (₹) must be at least 0' },
    ],
    'School Address Line 1': [
        { type: EValidationType.REQUIRED, message: 'School Address Line 1 is required' },
        { type: EValidationType.STRING, message: 'School Address Line 1 must be text' },
    ],
    'Shipping City': [
        { type: EValidationType.REQUIRED, message: 'School City is required' },
        { type: EValidationType.STRING, message: 'School City must be text' },
    ],
    'Shipping Pincode': [
        { type: EValidationType.REQUIRED, message: 'School Pincode is required' },
        { type: EValidationType.PINCODE, message: 'School Pincode must be a 6-digit number' },
    ],
    TAN: [
        { type: EValidationType.REQUIRED, message: 'TAN is required' },
        { type: EValidationType.STRING, message: 'TAN must be text' },
    ],
    GST: [
        { type: EValidationType.REQUIRED, message: 'GST is required' },
        { type: EValidationType.STRING, message: 'GST must be text' },
    ],
    PAN: [
        { type: EValidationType.REQUIRED, message: 'PAN is required' },
        { type: EValidationType.STRING, message: 'PAN must be text' },
    ],
    'Billing Name': [
        { type: EValidationType.REQUIRED, message: 'Billing Name is required' },
        { type: EValidationType.STRING, message: 'Billing Name must be text' },
    ],
    'Billing Address Line 1': [
        { type: EValidationType.REQUIRED, message: 'Billing Address Line 1 is required' },
        { type: EValidationType.STRING, message: 'Billing Address Line 1 must be text' },
    ],
}
/** National SBU Stages aligned with backend stage_id and stage_name */
export const allNationalStages: ILeadStageOption[] = [
    { value: ELeadStageTitle.S0, label: 'S0 — All Schools' },
    { value: ELeadStageTitle.S1, label: 'S1 — Prospecting' },
    { value: ELeadStageTitle.S2, label: 'S2 — Prospect Identified' },
    { value: ELeadStageTitle.S3, label: 'S3 — Demo Completed' },
    { value: ELeadStageTitle.S4, label: 'S4 — Proposal Submitted' },
    { value: ELeadStageTitle.S5, label: 'S5 — Contract Negotiation' },
    { value: ELeadStageTitle.S6, label: 'S6 — SSF Signed' },
    { value: ELeadStageTitle.S7, label: 'S7 — Deal Won' },
    { value: ELeadStageTitle.S8, label: 'S8 — Lead Lost' },
    { value: ELeadStageTitle.S9, label: 'S9 — Lead Deferred' },
]

export const EMAIL_PROPOSAL_SEND_EMAIL_TOOLTIP = 'Send email'
export const EMAIL_PROPOSAL_CONFIRM_BEFORE_SENDING_TOOLTIP = 'Please confirm before sending'
export const LEAD_STAGES = {
    ALL_SCHOOLS: 'All Schools',
    PROSPECTING: 'Prospecting',
    PROSPECT_IDENTIFIED: 'Prospect Identified',
    DEMO_COMPLETED: 'Demo Completed',
    PROPOSAL_SUBMITTED: 'Proposal Submitted',
    CONTRACT_NEGOTIATION: 'Contract Negotiation',
    SSF_SIGNED: 'SSF Signed',
    DEAL_WON: 'Deal Won',
    LEAD_LOST: 'Lead Lost',
    LEAD_DEFERRED: 'Lead Deferred',
    ALL: 'all',
}

/** Backend stage_id to stage_name for consistent display */
export const LEAD_STAGE_LABEL_BY_ID: Record<number, string> = {
    0: LEAD_STAGES.ALL_SCHOOLS,
    1: LEAD_STAGES.PROSPECTING,
    2: LEAD_STAGES.PROSPECT_IDENTIFIED,
    3: LEAD_STAGES.DEMO_COMPLETED,
    4: LEAD_STAGES.PROPOSAL_SUBMITTED,
    5: LEAD_STAGES.CONTRACT_NEGOTIATION,
    6: LEAD_STAGES.SSF_SIGNED,
    7: LEAD_STAGES.DEAL_WON,
    8: LEAD_STAGES.LEAD_LOST,
    9: LEAD_STAGES.LEAD_DEFERRED,
}

// Lead Information Constants
export const LEAD_PRODUCTS = ['ASSET', 'Mindspark', 'CARES'] as const
export const LEAD_CURRENCIES = ['INR (₹)', 'USD ($)', 'EUR (€)'] as const
export const LEAD_DEFAULT_CURRENCY = LEAD_CURRENCIES[0]
export const LEAD_ACADEMIC_YEARS = ['2024-2025', '2025-2026', '2026-2027'] as const
export const LEAD_CONVERSION_PROBABILITIES = ['High (50%)', 'Medium (25%)', 'Low (5%)'] as const
export const LEAD_FOCUS_TYPES = ['NSH Focus', 'SZM Focus', 'CEO Focus', 'CLO Focus'] as const
export const LEAD_CHANNEL_PARTNERS = [
    'TechnoNext Solutions',
    'EduCorp Partners',
    'Smart Solutions Ltd',
    'Academic Excellence Partners',
    'Digital Education Hub',
] as const

// Icon Constants
export const ICON_NAMES = {
    EDIT: 'edit',
    CHECK: 'check',
    PEOPLE: 'people',
    EMAIL: 'email',
    PHONE: 'phone',
} as const

// Grade Management Component Constants
export const GRADE_MANAGEMENT_CONSTANTS = {
    ICONS: {
        KEYBOARD_ARROW_UP: 'keyboard_arrow_up',
        KEYBOARD_ARROW_DOWN: 'keyboard_arrow_down',
    },
    BUTTON_TEXT: {
        HIDE: 'Hide',
        SHOW: 'Show',
        UPDATE: 'Update',
        SAVE: 'Save',
    },
} as const

export const DIALOG_INLINE_MESSAGE_CONFIG: {
    DEFAULT_DURATION_MS: number
    MAX_STACK: number
    COUNTDOWN_INTERVAL_MS: number
} = {
    DEFAULT_DURATION_MS: 6000,
    MAX_STACK: 5,
    COUNTDOWN_INTERVAL_MS: 1000,
}

export const EMAIL_RECIPIENT_ADDRESS_DELIMITER = ',' as const
